import { useEffect } from 'react'
import { useForm, useStore } from "@tanstack/react-form";
import { z } from "zod";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { SalesRepSelect, SALES_REP_UNASSIGNED } from "@/features/customers/sales-rep-select";
import { LEAD_SOURCE_LABELS, type LeadSource } from "@/types/customers";
import { useCustomers } from "@/features/customers/hooks";

// Sentinel select values for "not set" (mapped back to "" on submit).
export const SOURCE_UNSET = "__unset__";
export const REFERRER_UNSET = "__unset__";

// Email is format-checked only when non-empty. At least one name (or a
// company) is required so the customer list never shows anonymous rows.
const customerFields = z.object({
  first_name: z.string(),
  last_name: z.string(),
  company_name: z.string().optional(),
  contact_email: z.string().email("Invalid email address").or(z.literal("")),
  phone: z.string().optional(),
  assigned_sales_rep: z.string(),
  status: z.enum(["lead", "active", "inactive"]),
  lead_source: z.string(),
  referred_by: z.string(),
});

export const customerFormSchema = customerFields.refine(
  (v) => v.first_name.trim() || v.last_name.trim() || v.company_name?.trim(),
  { message: "Enter at least a first name, last name, or company.", path: ["first_name"] },
);

export type CustomerFormValues = z.infer<typeof customerFields>;

const fieldSchemas = customerFields.shape;

interface CustomerFormProps {
  /** Reports dirty state up so the host dialog can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void
  defaultValues?: Partial<CustomerFormValues>;
  /** The edited customer's id, when editing — excluded from the referrer list. */
  customerId?: string;
  onSubmit: (values: CustomerFormValues) => void;
  isPending?: boolean;
  submitLabel?: string;
}

export function CustomerForm({ onDirtyChange, defaultValues, customerId, onSubmit, isPending, submitLabel = "Save" }: CustomerFormProps) {
  // The referred-by dropdown lists existing customers (excluding the customer
  // being edited — nobody refers themselves).
  const { data: customers = [] } = useCustomers();

  const initialValues: CustomerFormValues = {
    first_name: "",
    last_name: "",
    company_name: "",
    contact_email: "",
    phone: "",
    assigned_sales_rep: "",
    status: "lead",
    lead_source: SOURCE_UNSET,
    referred_by: REFERRER_UNSET,
    ...defaultValues,
  };

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onSubmit: customerFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        assigned_sales_rep:
          value.assigned_sales_rep === SALES_REP_UNASSIGNED ? "" : value.assigned_sales_rep,
        // Select sentinels map back to "not set" for the API.
        lead_source: value.lead_source === SOURCE_UNSET ? "" : value.lead_source,
        referred_by: value.referred_by === REFERRER_UNSET ? "" : value.referred_by,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => state.isDirty)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <FormField
            name="first_name"
            validators={{ onChange: fieldSchemas.first_name, onSubmit: fieldSchemas.first_name }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl><Input placeholder="Jane" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="last_name"
            validators={{ onChange: fieldSchemas.last_name }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl><Input placeholder="Doe" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          name="company_name"
          validators={{ onChange: fieldSchemas.company_name }}
          render={({ field }) => (
            <FormItem>
                <FormLabel>Company</FormLabel>
              <FormControl><Input placeholder="Acme Marine" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="contact_email"
          validators={{ onChange: fieldSchemas.contact_email }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl><Input type="email" placeholder="jane@acme.com" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="phone"
          validators={{ onChange: fieldSchemas.phone }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl><Input placeholder="555-1234" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="assigned_sales_rep"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sales Rep</FormLabel>
              <FormControl>
                <SalesRepSelect
                  value={(field.value as string) || ""}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="status"
          validators={{ onChange: fieldSchemas.status }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} value={field.value as string}>
                <FormControl><SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="lead_source"
          validators={{ onChange: fieldSchemas.lead_source }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lead Source</FormLabel>
              <Select onValueChange={field.onChange} value={field.value as string}>
                <FormControl><SelectTrigger><SelectValue placeholder="Unknown" /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value={SOURCE_UNSET}>Unknown</SelectItem>
                  {(Object.entries(LEAD_SOURCE_LABELS) as [LeadSource, string][]).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="referred_by"
          validators={{ onChange: fieldSchemas.referred_by }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Referred By</FormLabel>
              <Select onValueChange={field.onChange} value={field.value as string}>
                <FormControl><SelectTrigger><SelectValue placeholder="No referrer" /></SelectTrigger></FormControl>
                <SelectContent>
                  <SelectItem value={REFERRER_UNSET}>No referrer</SelectItem>
                  {customers
                    .filter((c) => c.id !== customerId)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {`${c.first_name} ${c.last_name}`.trim() || c.company_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  );
}
